// Type definitions for the Uniswap integration

// Supported blockchain networks
export enum SupportedChains {
  BASE_SEPOLIA = 84532,
  SEPOLIA = 11155111,
  POLYGON_MAINNET = 137,
  BASE_MAINNET = 8453
}

export function getSupportedChain(chainId: number) {
  if (chainId === CHAIN_CONFIG[SupportedChains.BASE_MAINNET].id ) {
    return SupportedChains.BASE_MAINNET
  }
  if (chainId === CHAIN_CONFIG[SupportedChains.SEPOLIA].id ) {
    return SupportedChains.SEPOLIA
  }
  if (chainId === CHAIN_CONFIG[SupportedChains.POLYGON_MAINNET].id ) {
    return SupportedChains.POLYGON_MAINNET
  }
  if (chainId === CHAIN_CONFIG[SupportedChains.BASE_SEPOLIA].id ) {
    return SupportedChains.BASE_SEPOLIA
  }
  return undefined
}

export const ROUTER_AS_RECIPIENT = "0x0000000000000000000000000000000000000002";

// Hex string type for Ethereum addresses and data
export type THexString = `0x${string}`;

// Contract command structure for Universal Router
export type TContractCommand = {
  command: THexString;
  inputs: THexString[];
}

// Chain configuration structure
export interface IChainConfig {
  id: number;
  rpcUrl: string;
  name: string;
  quoterV3Address: string;
  quoterV4Address: string;
  factoryAddress: string;
  swapRouterAddress: string;
  routerUniversalAddress: THexString;
  weth_address: THexString;
  mph_address: THexString;
}

// Quote result interface
export interface IQuoteResult {
  amountIn: number;
  amountOut: number;
  tokenIn: string;
  tokenOut: string;
  fee?: number;
  exchangeRate?: number;
  gasEstimate: bigint;
}

// Pool state interface
export interface IPoolState {
  sqrtPriceX96: bigint;
  liquidity: bigint;
  tickCurrent: number;
}


export interface ISwapParamsNew {
  tokenIn?: TTokenInfo;
  tokenOut?: TTokenInfo;
  amountIn: number;
  recipient: THexString;
  slippageTolerance: number; // 0.5 = 0.5%
  deadline: number; // Unix timestamp in seconds
}


// Swap parameters interface
export interface ISwapParams {
  tokenIn?: THexString;
  tokenOut?: THexString;
  amountIn: number;
  recipient: THexString;
  slippageTolerance: number; // 0.5 = 0.5%
  deadline: number; // Unix timestamp in seconds
}

// Router commands structure
export interface IRouterCommand {
  command: THexString;
  args: any[];
}

/**
 * Configuration for supported chains
 */
export const CHAIN_CONFIG: Record<SupportedChains, IChainConfig> = {
  [SupportedChains.BASE_SEPOLIA]: {
    id: 84532,
    name: 'Base Sepolia',
    rpcUrl: process.env.BASE_SEPOLIA_RPC_URL || '',
    quoterV3Address: '0xC5290058841028F1614F3A6F0F5816cAd0df5E27',
    quoterV4Address: '0x4a6513c898fe1b2d0e78d3b0e0a4a151589b1cba', 
    factoryAddress: '0x4752ba5DBc23f44D87826276BF6Fd6b1C372aD24',
    swapRouterAddress: '0x94cC0AaC535CCDB3C01d6787D6413C739ae12bc4',
    routerUniversalAddress: '0x492e6456d9528771018deb9e87ef7750ef184104',
    weth_address: '0x4200000000000000000000000000000000000006',
    mph_address: '0xD8C5054B8d3162ad04d50b1bC2C74D6C9C0933d7'
  },
  [SupportedChains.SEPOLIA]: {
    id: 11155111,
    name: 'Sepolia',
    rpcUrl: process.env.SEPOLIA_RPC_URL || '',
    quoterV3Address: '0xEd1f6473345F45b75F8179591dd5bA1888cf2FB3',
    quoterV4Address: '0x3a9d48ab9751398bbfa63ad67599bb04e4bdf98b', 
    factoryAddress: '0x0227628f3F023bb0B980b67D528571c95c6DaC1c',
    swapRouterAddress: '0x3bFA4769FB09eefC5a80d6E87c3B9C650f7Ae48E',
    routerUniversalAddress: '0x3fC91A3afd70395Cd496C647d5a6CC9D4B2b7FAD',
    weth_address: '0xfFf9976782d46CC05630D1f6eBAb18b2324d6B14',
    mph_address: '0x49747DC699f0b71EcE77c6F0170F88e704c561Cf'
  },
  [SupportedChains.POLYGON_MAINNET]: {
    id: 137,
    rpcUrl: process.env.POLYGON_MAINNET_RPC_URL || '',
    name: 'Polygon',
    quoterV3Address: '0x61fFE014bA17989E743c5F6cB21bF9697530B21e',
    quoterV4Address: '0xb3d5c3dfc3a7aebff71895a7191796bffc2c81b9', 
    factoryAddress: '0x1F98431c8aD98523631AE4a59f267346ea31F984',
    swapRouterAddress: '0x68b3465833fb72A70ecDF485E0e4C7bD8665Fc45',
    routerUniversalAddress: '0x1095692a6237d83c6a72f3f5efedb9a670c49223',
    weth_address: '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270', // WPOL
    mph_address: '0x65C9e3289e5949134759119DBc9F862E8d6F2fBE'
  },
  [SupportedChains.BASE_MAINNET]: {
    id: 8453,
    name: 'Base',
    rpcUrl: process.env.BASE_MAINNET_RPC_URL || '',
    quoterV3Address: '0x3d4e44Eb1374240CE5F1B871ab261CD16335B76a',
    quoterV4Address: '0x0d5e0f971ed27fbff6c2837bf31316121532048d', 
    factoryAddress: '0x33128a8fC17869897dcE68Ed026d694621f6FDfD',
    swapRouterAddress: '0x2626664c2603336E57B271c5C0b26F421741e481',
    routerUniversalAddress: '0x6ff5693b99212da76ad316178a184ab56d299b43',
    weth_address: '0x4200000000000000000000000000000000000006',
    mph_address:  '0x0000000000000000000000000000000000000000' // not deployed yet!
  },
};

export enum FeeAmount {
  LOWEST = 100,
  LOW_200 = 200,
  LOW_300 = 300,
  LOW_400 = 400,
  LOW = 500,
  MEDIUM = 3000,
  HIGH = 10000
}

export interface TTokenInfo {
  address: THexString;
  name: string;
  symbol: string;
  decimals: number;
  eth_address: THexString;
  balance: bigint;
}

export const PERMIT2_ADDRESS = "0x000000000022D473030F116dDEE9F6B43aC78BA3";

export const RouterCommands: Record<string, IRouterCommand> = {
  V4_SWAP: {
    command: '0x10' as THexString,
    args: [
      { type: 'address' },  // recipient
      { type: 'uint256' },  // amountIn
      { type: 'uint256' },  // amountOutMin
      { type: 'tuple', components: [
        { type: 'address', name: 'currency0' },
        { type: 'address', name: 'currency1' },
        { type: 'uint24', name: 'fee' },
        { type: 'int24', name: 'tickSpacing' },
        { type: 'address', name: 'hooks' }
      ] },  // poolKey
      { type: 'bool' },     // zeroForOne
      { type: 'bytes' },    // hookData
      { type: 'bool' }      // payerIsUser
    ] as any[]
  },
  V2_SWAP_EXACT_IN: {
    command: '0x08' as THexString,
    args: [
      { type: 'address' },  // recipient
      { type: 'uint256' },  // amountIn
      { type: 'uint256' },  // amountOutMin
      { type: 'address[]' }, // path
      { type: 'bool' }      // payerIsUser
    ] as any[]
  },
  
  V3_SWAP_EXACT_IN: {
    command: '0x00' as THexString,
    args: [
      { type: 'address' },  // recipient
      { type: 'uint256' },  // amountIn
      { type: 'uint256' },  // amountOutMin
      { type: 'bytes' },    // path
      { type: 'bool' }      // payerIsUser
    ] as any[]
  },
  WRAP_ETH: {
    command: '0x0b' as THexString,
    args: [
      { type: 'address' },  // address
      { type: 'uint256' },  // amount
    ] as any[]
  },
  SWEEP: {
    command: '0x04' as THexString, // Fixed the command code from 0x0b to 0x04
    args: [
      { type: 'address' },  // The ERC20 token to sweep (or Constants.ETH for ETH)
      { type: 'address' },  // The recipient of the sweep
      { type: 'uint256' },  // The minimum required tokens to receive from the sweep
    ] as any[]
  },
  UNWRAP_ETH: {
    command: '0x0c' as THexString,
    args: [
      { type: 'address' },  // address
      { type: 'uint256' },  // amount
    ] as any[]
  },
  PERMIT2_PERMIT: {
    command: '0x0a' as THexString,
    args: [
      { type: 'tuple', components: [
        { type: 'tuple', components: [
          { type: 'address', name: 'token' },
          { type: 'uint160', name: 'amount' },
          { type: 'uint48', name: 'expiration' },
          { type: 'uint48', name: 'nonce' }
        ], name: 'details' },
        { type: 'address', name: 'spender' },
        { type: 'uint256', name: 'sigDeadline' }
      ] },
      { type: 'bytes' }
    ] as any[]
  }
}
